// ── Command Center custom-category sharing — API client ───────────────────────
// Base: process.env.REACT_APP_MyAMP_Url + /api/command-center
// Auth: Bearer authToken from localStorage — the same token the Command Center
// config panel already sends to /api/command-center/custom-category-mode.
//
// Category-level counterpart to the agent-share helpers in
// features/claudeagents/claudeAgentApi.ts (shareAgent & co.). Two ways to
// share: pick teammates in your own agency and each gets their own copy right
// away (shareCategoryWithUsers), or mint a link whose opener installs their own
// copy (createCategoryShare → resolve → import). Never a reference to the
// sharer's row. A link stops working when it is revoked or when the category
// it was made from is deleted; copies already given to teammates are theirs.
// Responses are plain JSON — `{ error }` with a non-2xx status on failure —
// unlike the { success, data } envelope claude-managed uses.

const BASE = `${process.env.REACT_APP_MyAMP_Url ?? ''}/api/command-center`;

export type CategoryShareScope = 'agency' | 'anyone';

// Assignee kinds as they travel in a snapshot (routes/command_center_category_shares.py
// normalizes them the way the config panel and the task pipeline read them):
// 0 = User, 1 = AI Agent, 2 = a Digital Workforce agent (only its NAME travels).
export const CATEGORY_ASSIGNEE_USER = 0;
export const CATEGORY_ASSIGNEE_AI = 1;
export const CATEGORY_ASSIGNEE_DW_AGENT = 2;

// Run modes, same integers the config panel stores (KANBAN_MODE_INT there).
export const CATEGORY_MODE_AUTO = 0;
export const CATEGORY_MODE_MANUAL = 1;
export const CATEGORY_MODE_DISABLED = 2;

export interface CategoryShareRecord {
  id: number;
  token: string;
  scope: CategoryShareScope;
  created_at?: string | null;
}

// What a share carries. Deliberately user-neutral: no ids, no run mode (a
// per-user routing preference the recipient picks on import), no agent_id
// (Digital Workforce agent ids are per user — only the agent's NAME travels,
// so the recipient can be matched to, or offered, one of their own agents).
export interface CategoryShareSnapshot {
  name: string;
  title: string;
  description: string;
  trigger_label: string;
  completion_label: string;
  identification_prompt: string;
  execution_prompt: string;
  // 0 = a specific inbox label selects the category, 1 = every task is
  // evaluated via the identification prompt (MomentumAMPApi CategoryMatchType).
  category_match_type: number;
  assignee_type: number;
  agent_name: string;
}

export interface ImportedCategory {
  id: number;
  name: string;
  title: string;
  description: string;
  trigger_label: string;
  completion_label: string;
  identification_prompt: string;
  execution_prompt: string;
  category_match_type: number;
  assignee_type: number;
  agent_id: string;
  agent_name: string;
  mode: number;
}

// A teammate the caller can share with: an active user in the same agency.
export interface AgencyUser {
  id: number;
  name: string;
  email: string;
}

// created      — the teammate now has their own copy (category_id)
// already_has  — they keep the category of that name they already had
// owner        — that was the sharer themself; nothing to do
// failed       — the copy could not be created for them; the rest went ahead
export type DirectShareStatus = 'created' | 'already_has' | 'owner' | 'failed';

export interface DirectShareResult {
  user_id: number;
  name: string;
  status: DirectShareStatus;
  reason?: string;
  category_id?: number;
  // Who the copy is assigned to: their same-named agent, "AI Agent", or "them".
  assigned_to?: string;
  agent_matched?: boolean;
}

export interface CategoryDirectShareRecord {
  user_id: number;
  name: string;
  created_at: string | null;
  category_id: number | null;
}

function token(): string {
  return localStorage.getItem('authToken') ?? '';
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...((init?.headers as Record<string, string>) ?? {}),
    },
  });
  let json: any = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  if (!res.ok) {
    const message = json && typeof json.error === 'string' && json.error.trim() ? json.error : `HTTP ${res.status}`;
    throw new Error(message);
  }
  return json as T;
}

// ── Share with teammates ──────────────────────────────────────────────────────

export const listAgencyUsers = () =>
  api<{ users: AgencyUser[] }>('/agency-users').then((r) => r.users ?? []);

/** Gives each selected teammate their own copy right away. Every id is
 * re-checked server-side as an active member of the caller's agency; one id
 * outside it fails the whole request (403) and nothing is created. */
export const shareCategoryWithUsers = (categoryId: number, userIds: number[]) =>
  api<{ results: DirectShareResult[]; shared_name: string }>(`/categories/${categoryId}/share-with-users`, {
    method: 'POST',
    body: JSON.stringify({ user_ids: userIds }),
  });

export const listCategoryDirectShares = (categoryId: number) =>
  api<{ recipients: CategoryDirectShareRecord[] }>(`/categories/${categoryId}/direct-shares`).then(
    (r) => r.recipients ?? [],
  );

// ── Share via link: owner side ────────────────────────────────────────────────

export const createCategoryShare = (categoryId: number, scope: CategoryShareScope) =>
  api<CategoryShareRecord>(`/categories/${categoryId}/share`, {
    method: 'POST',
    body: JSON.stringify({ scope }),
  });

export const listCategoryShares = (categoryId: number) =>
  api<{ shares: CategoryShareRecord[] }>(`/categories/${categoryId}/shares`).then((r) => r.shares ?? []);

export const revokeCategoryShare = (shareId: number) =>
  api<{ message: string }>(`/category-shares/${shareId}`, { method: 'DELETE' });

/** Re-freezes the link's snapshot from the category's current definition —
 * same token, same URL — for "I edited the category since sharing it" without
 * invalidating links already sent out. */
export const refreshCategoryShare = (shareId: number) =>
  api<{ id: number; message: string }>(`/category-shares/${shareId}/refresh`, { method: 'POST' });

export const regenerateCategoryShare = (shareId: number) =>
  api<CategoryShareRecord>(`/category-shares/${shareId}/regenerate`, { method: 'POST' });

// ── Share via link: recipient side ────────────────────────────────────────────

export const resolveCategoryShare = (tokenValue: string) =>
  api<{ share_id: number; snapshot: CategoryShareSnapshot }>(`/category-shares/resolve/${tokenValue}`);

/** Creates the caller's own copy. `name` resolves a collision with an existing
 * category (the server answers 409 "choose a different name"); `mode` is the
 * copy's run mode (Wait for Human Approval when omitted); `agentId` /
 * `agentName` is one of the CALLER's Digital Workforce agents, or omitted to
 * keep a user-neutral assignee (AI Agent when the shared category had one,
 * otherwise the User). */
export const importCategoryShare = (
  tokenValue: string,
  options: { name: string; mode?: number; agentId?: string; agentName?: string },
) =>
  api<ImportedCategory>(`/category-shares/${tokenValue}/import`, {
    method: 'POST',
    body: JSON.stringify({
      name: options.name,
      mode: options.mode ?? CATEGORY_MODE_MANUAL,
      agent_id: options.agentId ?? '',
      agent_name: options.agentName ?? '',
    }),
  });

export const categoryShareUrl = (tokenValue: string) => `${window.location.origin}/share-category/${tokenValue}`;
